import { useState } from 'react';
import { useStepData } from '@/hooks/useStepData';
import { Stepper, StepperButtons, StepperContent, StepperProgress } from '@/components/Stepper';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from 'react-router-dom';

import ActionStep from '@/components/CreateMonitorSteps/ActionStep';
import ConfigurationStep from '@/components/CreateMonitorSteps/ConfigurationStep';
import PreviewStep from '@/components/CreateMonitorSteps/PreviewStep';
import ScheduleStep from '@/components/CreateMonitorSteps/ScheduleStep';

export const MonitorForm = ({ initialData = null, onSubmit }) => {
    const initialSteps = [
        {
            key: 'actionType',
            name: 'Action Type',
            data: '',
            isComplete: false,
            validation: [
                { field: 'actionType', required: true }
            ],
            component: ActionStep
        },
        {
            key: 'configuration',
            name: 'Configuration',
            data: {
                organizationId: '',
                organizationName: '',
                method: 'GET',
                protocol: 'https:',
                hostname: '',
                requestHeaders: '',
                requestBody: ''
            },
            isComplete: false,
            validation: [
                { field: 'organizationId', required: true }
            ],
            component: ConfigurationStep
        },
        {
            key: 'schedule',
            name: 'Schedule & Alerts',
            data: { interval: 15 },
            isComplete: false,
            validation: [
                { field: 'interval', required: true, regex: /^\d+$/ }
            ],
            component: ScheduleStep
        },
        {
            key: 'preview',
            name: 'Preview',
            data: {},
            isComplete: false,
            validation: [],
            component: PreviewStep
        }
    ];
    
    // If we have initialData, populate the steps (used for editing)
    if (initialData) {
        initialSteps[0].data = initialData.actionType;
        initialSteps[1].data = {
            name: initialData.name,
            method: initialData.settings.method || 'GET',
            protocol: initialData.settings.protocol || 'https:',
            hostname: initialData.settings.hostname + (initialData.settings.path ? '/' + initialData.settings.path : ''),
            requestHeaders: initialData.settings.requestHeaders || '',
            requestBody: initialData.settings.requestBody || '',
            organizationId: initialData.organizationId || '',
            organizationName: initialData.organizationName || '',
            url: initialData.settings.url || '',
            prompt: initialData.settings.prompt || ''
        };
        initialSteps[2].data = { interval: initialData.interval };

        // Mark steps as complete based on validation rules
        initialSteps.forEach(step => {
            step.isComplete = step.validation.every(({ field, required, regex }) => {
                const value = typeof step.data === 'object' ? step.data[field] : step.data;
                if (required && (value === undefined || value === '')) {
                    return false;
                }
                if (regex && !regex.test(value)) {
                    return false;
                }
                return true;
            });
        });

        // Preview step is always complete when editing
        initialSteps[3].isComplete = true;
    }

    const { steps, updateStepData, getStepData, isStepComplete, isAutoSaving } = useStepData(initialSteps, initialData?._id);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();
    const navigate = useNavigate();

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const configData = getStepData('configuration');
            const actionType = getStepData('actionType');
            
            let monitorData = {
                name: configData.name || 'Unnamed Monitor',
                actionType: actionType,
                organizationId: configData.organizationId,
                interval: getStepData('schedule').interval
            };

            if (actionType === 'ping') {
                const [hostname, ...pathParts] = configData.hostname.split('/');
                const path = pathParts.length > 0 ? pathParts.join('/') : '';
                
                monitorData.settings = {
                    method: configData.method,
                    protocol: configData.protocol,
                    hostname: hostname,
                    path: path,
                    requestHeaders: configData.requestHeaders,
                    requestBody: configData.requestBody
                };
            } else if (actionType === 'browser_agent') {
                monitorData.settings = {
                    url: configData.url,
                    prompt: configData.prompt
                };
            }

            const response = await onSubmit(monitorData);
            toast({
                description: initialData ? "Monitor updated successfully" : "Monitor created successfully",
            });
            navigate(`/monitors/${response.data._id}`);
        } catch (error) {
            console.error('Error saving monitor:', error);
            toast({
                description: "Failed to save monitor",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Stepper steps={steps} currentStep={currentStep} setCurrentStep={setCurrentStep}>
                <Card className="border-none shadow-none flex flex-col justify-between h-[calc(100vh-4rem)]">
                    <CardHeader className="border-b">
                        <StepperProgress />
                    </CardHeader>
                    <CardContent className="flex-grow overflow-hidden p-0">
                        <ScrollArea className="h-full w-full">
                            <div className="mx-6 my-8">
                                <StepperContent>
                                    {steps.map((step, index) => {
                                        const StepComponent = step.component;
                                        return (
                                            <StepComponent
                                                key={step.key}
                                                stepKey={step.key}
                                                updateStepData={updateStepData}
                                                getStepData={getStepData}
                                                isComplete={isStepComplete(step.key)}
                                            />
                                        );
                                    })}
                                </StepperContent>
                            </div>
                        </ScrollArea>
                    </CardContent>
                    <CardFooter className="border-t p-6">
                        <StepperButtons onSubmit={handleSubmit} loading={ loading } />
                    </CardFooter>
                </Card>
            </Stepper>
        </motion.div>
    );
}; 